import { useState, useCallback } from 'react';
import { Invoice } from '../../domain/entities/Invoice';
import { UseCaseProvider } from '../../application/useCases/UseCaseProvider';

/**
 * Hook personalizado para la búsqueda de facturas
 */
export const useInvoiceSearch = () => {
  // Estado para la factura encontrada
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  
  // Estado para el error
  const [error, setError] = useState<string | null>(null);
  
  // Estado para indicar si se está cargando
  const [loading, setLoading] = useState(false);
  
  // Estado para validación de reCAPTCHA
  const [captchaValid, setCaptchaValid] = useState(false);
  
  /**
   * Función para buscar una factura por su número
   */
  const searchInvoice = useCallback(async (invoiceNumber: string) => {
    // Reseteamos el estado
    setError(null);
    setLoading(true);
    
    try {
      // Obtenemos la instancia del caso de uso a través del proveedor
      const findInvoiceUseCase = UseCaseProvider.getFindInvoiceUseCase();
      
      // Ejecutamos el caso de uso
      const result = await findInvoiceUseCase.execute(invoiceNumber);
      
      if (result.success) {
        setInvoice(result.invoice);
      } else {
        setError(result.message || 'Error desconocido al buscar la factura');
        setInvoice(null);
        // Cuando no se encuentra la factura, reseteamos la validación del captcha
        setCaptchaValid(false);
      }
    } catch (err) {
      setError('Error al procesar la solicitud. Por favor, inténtelo de nuevo.');
      setInvoice(null);
      // En caso de error, también reseteamos la validación del captcha
      setCaptchaValid(false);
      console.error('Error en useInvoiceSearch:', err);
    } finally {
      setLoading(false);
    }
  }, []);
  
  /**
   * Función para validar el reCAPTCHA
   */
  const validateCaptcha = useCallback((token: string) => {
    // En una implementación real, se enviaría el token al backend para validarlo
    // Por ahora, simplemente verificamos que existe un token
    if (token) {
      setCaptchaValid(true);
    } else {
      setCaptchaValid(false);
    }
  }, []);
  
  /**
   * Función para resetear el captcha
   */
  const resetCaptcha = useCallback(() => {
    setCaptchaValid(false);
  }, []);
  
  /**
   * Función para resetear el estado
   */
  const reset = useCallback(() => {
    setInvoice(null);
    setError(null);
    setLoading(false);
    setCaptchaValid(false);
  }, []);
  
  return {
    invoice,
    error,
    loading,
    captchaValid,
    searchInvoice,
    validateCaptcha,
    resetCaptcha,
    reset
  };
}; 